import {
  CommsHealth,
  Provider,
  ProviderConfigPatch,
  NumberInv,
  Template,
  TestSendReq,
  Event,
  MessageRow,
  DLQReplayReq,
} from '../types/nativeComms';

const DEMO = import.meta.env.VITE_ENGINEERING_BRIDGE_DEMO === 'true';
const API_BASE = '/api/native-comms';

// Mock data for demo mode
const mockCommsHealth: CommsHealth = {
  asOf: new Date().toISOString(),
  deliveredPct: 98.7,
  sendSuccessPct: 99.2,
  dlrRatePct: 97.8,
  dlrLatencyMs: { p50: 1200, p95: 3500 },
  bouncePct: 0.8,
  optOutPct: 0.3,
  queue: { depth: 45, oldestAgeSec: 180, retryRatePct: 2.1 },
  costPerMessageUsd: 0.0042,
  errorsByClass: [
    { class: '4xx_content', count: 12 },
    { class: '4xx_optout', count: 8 },
    { class: '5xx_provider', count: 3 },
    { class: 'timeout', count: 7 },
    { class: 'validation', count: 5 },
  ],
};

const mockProviders: Provider[] = [
  {
    id: 'twilio-sms',
    name: 'Twilio SMS',
    region: 'us-east-1',
    status: 'healthy',
    maxRps: 1000,
    currentRps: 450,
    errRatePct: 0.2,
    costWeight: 1.0,
    priority: 1,
  },
  {
    id: 'twilio-smpp',
    name: 'Twilio SMPP',
    region: 'us-west-2',
    status: 'healthy',
    maxRps: 500,
    currentRps: 180,
    errRatePct: 0.1,
    costWeight: 0.8,
    priority: 2,
  },
  {
    id: 'sendgrid-email',
    name: 'SendGrid Email',
    region: 'us-central-1',
    status: 'degraded',
    maxRps: 2000,
    currentRps: 1200,
    errRatePct: 1.5,
    costWeight: 0.5,
    priority: 3,
  },
];

const mockNumbers: NumberInv[] = [
  {
    id: 'num-1',
    e164: '+15551234567',
    channel: 'sms',
    verified: true,
    tenant: 'tyler',
    rpsCap: 100,
  },
  {
    id: 'num-2',
    e164: '+15551234568',
    channel: 'sms',
    verified: true,
    tenant: 'hivecore',
    rpsCap: 150,
  },
  {
    id: 'num-3',
    e164: '+15551234569',
    channel: 'voice',
    verified: false,
    rpsCap: 50,
  },
];

const mockTemplates: Template[] = [
  {
    id: 'template-1',
    name: 'Welcome SMS',
    channel: 'sms',
    version: '1.2',
    body: 'Welcome {{name}}! Your account {{account}} is ready.',
    vars: ['name', 'account'],
    updatedAt: '2024-01-15T10:30:00Z',
  },
  {
    id: 'template-2',
    name: 'Password Reset',
    channel: 'email',
    version: '2.1',
    body: 'Hi {{name}}, reset your password: {{resetLink}}',
    vars: ['name', 'resetLink'],
    updatedAt: '2024-01-14T15:45:00Z',
  },
];

const mockEvents: Event[] = [
  {
    id: 'evt-1',
    kind: 'DLR',
    providerId: 'twilio-sms',
    status: 'DELIVERED',
    ts: new Date(Date.now() - 5000).toISOString(),
    meta: { messageId: 'msg-123', to: '+15551234567' },
  },
  {
    id: 'evt-2',
    kind: 'MO',
    providerId: 'twilio-sms',
    status: 'RECEIVED',
    ts: new Date(Date.now() - 15000).toISOString(),
    meta: { from: '+15551234567', body: 'STOP' },
  },
];

const mockMessages: MessageRow[] = [
  {
    id: 'msg-1',
    tenant: 'tyler',
    to: '+15551234567',
    from: '+15551234568',
    templateId: 'template-1',
    status: 'DELIVERED',
    attempts: 1,
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:02:00Z',
  },
  {
    id: 'msg-2',
    tenant: 'hivecore',
    to: '+15551234569',
    templateId: 'template-2',
    status: 'QUEUED',
    attempts: 0,
    createdAt: '2024-01-15T10:05:00Z',
    updatedAt: '2024-01-15T10:05:00Z',
  },
];

// API functions
export const nativeCommsAPI = {
  // Health
  async getHealth(): Promise<CommsHealth> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
      return mockCommsHealth;
    }
    
    const response = await fetch(`${API_BASE}/health`);
    if (!response.ok) throw new Error('Failed to fetch health data');
    return response.json();
  },

  // Providers
  async getProviders(): Promise<Provider[]> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return mockProviders;
    }
    
    const response = await fetch(`${API_BASE}/providers`);
    if (!response.ok) throw new Error('Failed to fetch providers');
    return response.json();
  },

  async updateProviderConfig(providerId: string, config: ProviderConfigPatch): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 200));
      console.log('Demo: Updating provider config', { providerId, config });
      return;
    }
    
    const response = await fetch(`${API_BASE}/providers/${providerId}/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    if (!response.ok) throw new Error('Failed to update provider config');
  },

  // Numbers
  async getNumbers(): Promise<NumberInv[]> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 400));
      return mockNumbers;
    }
    
    const response = await fetch(`${API_BASE}/numbers`);
    if (!response.ok) throw new Error('Failed to fetch numbers');
    return response.json();
  },

  // Templates
  async getTemplates(): Promise<Template[]> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return mockTemplates;
    }
    
    const response = await fetch(`${API_BASE}/templates`);
    if (!response.ok) throw new Error('Failed to fetch templates');
    return response.json();
  },

  async testSend(request: TestSendReq): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Demo: Test send', request);
      return;
    }
    
    const response = await fetch(`${API_BASE}/test-send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error('Failed to send test message');
  },

  // Events
  async getEvents(since?: string): Promise<Event[]> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 200));
      return mockEvents;
    }
    
    const params = since ? `?since=${since}` : '';
    const response = await fetch(`${API_BASE}/events${params}`);
    if (!response.ok) throw new Error('Failed to fetch events');
    return response.json();
  },

  // Messages
  async getMessages(status?: string, query?: string): Promise<MessageRow[]> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return mockMessages;
    }
    
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (query) params.append('q', query);
    
    const response = await fetch(`${API_BASE}/messages?${params}`);
    if (!response.ok) throw new Error('Failed to fetch messages');
    return response.json();
  },

  // DLQ Replay
  async replayDLQ(request: DLQReplayReq): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Demo: DLQ replay', request);
      return;
    }
    
    const response = await fetch(`${API_BASE}/dlq/replay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error('Failed to replay DLQ');
  },

  // Admin actions
  async pauseRouting(): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 800));
      console.log('Demo: Pausing routing');
      return;
    }
    
    const response = await fetch(`${API_BASE}/admin/pause-routing`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to pause routing');
  },

  async resumeRouting(): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 800));
      console.log('Demo: Resuming routing');
      return;
    }
    
    const response = await fetch(`${API_BASE}/admin/resume-routing`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to resume routing');
  },

  async drainQueue(): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Demo: Draining queue');
      return;
    }
    
    const response = await fetch(`${API_BASE}/admin/drain-queue`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to drain queue');
  },

  async failoverToBackup(): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 1200));
      console.log('Demo: Failing over to backup');
      return;
    }
    
    const response = await fetch(`${API_BASE}/admin/failover-backup`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to failover to backup');
  },

  async clearThrottleWindow(): Promise<void> {
    if (DEMO) {
      await new Promise(resolve => setTimeout(resolve, 600));
      console.log('Demo: Clearing throttle window');
      return;
    }
    
    const response = await fetch(`${API_BASE}/admin/clear-throttle`, { method: 'POST' });
    if (!response.ok) throw new Error('Failed to clear throttle window');
  },
};